/**
 * SMSHistoryView - Message history and broadcast logs
 */
import { Clock, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CAMPAIGN_NAMES } from './smsUtils';

export default function SMSHistoryView({
  history,
  historyTotal,
  batches,
  onRefresh
}) {
  return (
    <div className="space-y-6">
      {/* Recent Batches */}
      {batches.length > 0 && (
        <div className="bg-card rounded-2xl p-6">
          <h3 className="font-medium text-foreground mb-4">Recent Broadcasts</h3>
          <div className="space-y-3">
            {batches.slice(0, 5).map(batch => (
              <div key={batch.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {batch.recipient_type === 'campaign' 
                      ? `Campaign: ${CAMPAIGN_NAMES[batch.campaign_id] || 'All'}`
                      : batch.recipient_type === 'loyalty'
                      ? 'Loyalty Members'
                      : 'All Subscribers'
                    }
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(batch.started_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm text-green-500">{batch.sent_count} sent</p>
                    {batch.failed_count > 0 && (
                      <p className="text-xs text-red-500">{batch.failed_count} failed</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    batch.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                  }`}>
                    {batch.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Message History */}
      <div className="bg-card rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-foreground">Message History</h3>
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {history.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No messages sent yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Recipient</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Message</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                </tr>
              </thead>
              <tbody>
                {history.map(item => (
                  <tr key={item.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4">
                      <p className="text-sm font-medium text-foreground">{item.recipient_name}</p>
                      <p className="text-xs text-muted-foreground">{item.phone}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-foreground line-clamp-2 max-w-xs">{item.message}</p>
                      <p className="text-xs text-muted-foreground">{item.char_count} chars, {item.segment_count} segment(s)</p>
                    </td>
                    <td className="py-3 px-4">
                      {item.status === 'sent' ? (
                        <div>
                          <span className="flex items-center gap-1 text-green-500 text-sm">
                            <CheckCircle className="w-4 h-4" />
                            Sent
                          </span>
                          {item.delivery_status && (
                            <span className={`text-xs ${
                              item.delivery_status === 'delivered' ? 'text-green-600' :
                              item.delivery_status === 'failed' || item.delivery_status === 'undelivered' ? 'text-red-500' :
                              'text-amber-500'
                            }`}>
                              {item.delivery_status}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="flex items-center gap-1 text-red-500 text-sm">
                          <XCircle className="w-4 h-4" />
                          Failed
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {new Date(item.sent_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        <p className="text-xs text-muted-foreground mt-4 text-center">
          Showing {history.length} of {historyTotal} messages
        </p>
      </div>
    </div>
  );
}
